import React, { useMemo, useState } from "react";
import {
  Chart,
  Title,
  Series,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "@highcharts/react";
import "../../css/calculators/MortgageBorrow.css";
import FormattedNumberInput from "./HelperFunctions/FormattedNumberInput";
import SecondaryButton from "../designSystem/buttons/TertiaryButton";
import PrimaryButton from "../designSystem/buttons/PrimaryButton";
import CustomSelect from "../../frontend/designSystem/CustomSelect";


const initialState = {
  investmentGoal: "",
  amountAlreadyInvested: "",
  years: "",
  months: "",
  estimatedGrowth: "",
};

const ANNUAL_CHARGE = 0.75;

const growthOptions = [
  { value: "", label: "Please select" },
  { value: "1.5", label: "Low 1.5%" },
  { value: "4.5", label: "Medium 4.5%" },
  { value: "7.5", label: "High 7.5%" },
];

const cleanNumericInput = (value) => value.replace(/[^\d.]/g, "");

const parseNumericValue = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  return Number(value) || 0;
};

const formatCurrency = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const buildMonthlyInvestmentRequired = ({
  targetAmount,
  existingInvestment,
  years,
  months,
  annualGrowthRate,
}) => {
  const totalMonths = Number(years || 0) * 12 + Number(months || 0);

  if (!targetAmount || !annualGrowthRate || totalMonths <= 0) return null;

  const monthlyGrowthRate = annualGrowthRate / 100 / 12;
  const monthlyChargeRate = ANNUAL_CHARGE / 100 / 12;
  const netMonthlyRate = monthlyGrowthRate - monthlyChargeRate;

  const existingFutureValue =
    netMonthlyRate === 0
      ? Number(existingInvestment || 0)
      : Number(existingInvestment || 0) *
        Math.pow(1 + netMonthlyRate, totalMonths);

  const targetShortfall = Math.max(
    0,
    Number(targetAmount || 0) - existingFutureValue,
  );

  let requiredMonthlyInvestment = 0;

  if (targetShortfall > 0) {
    if (netMonthlyRate === 0) {
      requiredMonthlyInvestment = targetShortfall / totalMonths;
    } else {
      requiredMonthlyInvestment =
        targetShortfall /
        ((Math.pow(1 + netMonthlyRate, totalMonths) - 1) / netMonthlyRate);
    }
  }

  let fundValue = Number(existingInvestment || 0);
  const monthlyData = [{ month: 0, value: Number(fundValue.toFixed(2)) }];

  for (let monthIndex = 1; monthIndex <= totalMonths; monthIndex += 1) {
    fundValue += requiredMonthlyInvestment;
    fundValue *= 1 + monthlyGrowthRate;
    fundValue *= 1 - monthlyChargeRate;

    monthlyData.push({
      month: monthIndex,
      value: Number(fundValue.toFixed(2)),
    });
  }

  const finalValue = Number(fundValue.toFixed(2));
  const totalNewInvestments = Number(
    (requiredMonthlyInvestment * totalMonths).toFixed(2),
  );
  const investmentGrowth = Number(
    (
      finalValue -
      Number(existingInvestment || 0) -
      totalNewInvestments
    ).toFixed(2),
  );

  const chartIndexes = Array.from(
    new Set([
      0,
      Math.round(totalMonths / 3),
      Math.round((totalMonths * 2) / 3),
      totalMonths,
    ]),
  );

  const chartPoints = chartIndexes.map((index) => {
    const point = monthlyData[index];
    const pointYears = Math.floor(point.month / 12);
    const pointMonths = point.month % 12;

    return {
      label: `${pointYears} year${pointYears !== 1 ? "s" : ""}\n${pointMonths} month${pointMonths !== 1 ? "s" : ""}`,
      value: point.value,
    };
  });

  return {
    requiredMonthlyInvestment: Number(requiredMonthlyInvestment.toFixed(2)),
    finalValue,
    totalExisting: Number(existingInvestment || 0),
    totalNewInvestments,
    investmentGrowth,
    targetAmount: Number(targetAmount || 0),
    chartPoints,
  };
};

export default function InvestmentGoalMonthlyCalculator() {
  const [formData, setFormData] = useState(initialState);
  const [hasCalculated, setHasCalculated] = useState(false);

 const handleChange = (e) => {
   const { name, value } = e.target;

   if (["years", "months"].includes(name)) {
     setFormData((prev) => ({
       ...prev,
       [name]: cleanNumericInput(value),
     }));
     return;
   }

   setFormData((prev) => ({
     ...prev,
     [name]: value,
   }));
 };

  const handleCalculate = () => {
    setHasCalculated(true);
  };

  const handleClear = () => {
    setFormData(initialState);
    setHasCalculated(false);
  };

  const results = useMemo(() => {
    if (!hasCalculated) return null;

    return buildMonthlyInvestmentRequired({
      targetAmount: parseNumericValue(formData.investmentGoal),
      existingInvestment: parseNumericValue(formData.amountAlreadyInvested),
      years: parseNumericValue(formData.years),
      months: parseNumericValue(formData.months),
      annualGrowthRate: parseNumericValue(formData.estimatedGrowth),
    });
  }, [formData, hasCalculated]);

  const canCalculate =
    formData.investmentGoal &&
    formData.estimatedGrowth &&
    (parseNumericValue(formData.years) > 0 ||
      parseNumericValue(formData.months) > 0);

  const chartCategories = results?.chartPoints?.map((item) => item.label) || [];
  const chartData = results?.chartPoints?.map((item) => item.value) || [];

  return (
    <>
      <div className="borrow-form-wrap">
        <div className="borrow-field-group">
          <label className="borrow-label">Investment goal (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="investmentGoal"
              value={formData.investmentGoal}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  investmentGoal: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Amount invested already (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="amountAlreadyInvested"
              value={formData.amountAlreadyInvested}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  amountAlreadyInvested: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <div className="investment-duration-grid">
            <div>
              <label className="borrow-label">
                How long are you investing for? (years)
              </label>
              <input
                type="text"
                className="borrow-input"
                name="years"
                value={formData.years}
                onChange={handleChange}
                inputMode="numeric"
                autoComplete="off"
              />
            </div>

            <div>
              <label className="borrow-label">Months</label>
              <input
                type="text"
                className="borrow-input"
                name="months"
                value={formData.months}
                onChange={handleChange}
                inputMode="numeric"
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Estimated growth (%)</label>
          <div className="select-wrapper">
            {/* <select
              name="estimatedGrowth"
              value={formData.estimatedGrowth}
              onChange={handleChange}
              className="borrow-input"
            >
              {growthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select> */}
            <CustomSelect
              name="estimatedGrowth"
              options={growthOptions}
              value={formData.estimatedGrowth}
              onChange={handleChange}
              placeholder="Please select an estimated growth"
            />

          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 24,
            justifyContent: "flex-end",
          }}
        >
          <SecondaryButton onClick={handleClear} text="Clear" />
          <PrimaryButton onClick={handleCalculate} text="Calculate" />
        </div>
        <div className="result-divider investment-form-divider" />
      </div>

      {results && (
        <div className="borrow-result-wrap">
          <div className="result-container">
            <div className="title">Result</div>

            <div className="result-section">
              <p className="result-copy investment-top-copy">
                Charges assumed in the calculator are {ANNUAL_CHARGE}% per
                annum.
              </p>

              <p className="result-copy investment-top-copy">
                Charges are typically between 0.5% and 1.85% of the fund value
                per year depending on investments chosen and the amount
                invested.
              </p>
            </div>

            <div className="result-section">
              <div className="result-heading">
                You may need to invest each month
              </div>
              <div className="result-value">
                {formatCurrency(results.requiredMonthlyInvestment, 0)}
              </div>

              <div className="investment-chart-card">
                <Chart
                  containerProps={{
                    style: { width: "100%", height: "360px" },
                  }}
                >
                  <Title>Investment value over time</Title>

                  <XAxis categories={chartCategories} />
                  <YAxis />

                  <Series
                    type="area"
                    name="Your investments"
                    data={chartData}
                  />

                  <Tooltip />
                  <Legend />
                </Chart>
              </div>
            </div>

            <div className="title investment-based-on-title">
              The results are based on:
            </div>

            <div className="result-section">
              <div className="result-heading">Existing investments</div>
              <div className="result-value">
                {formatCurrency(results.totalExisting, 0)}
              </div>
            </div>

            <div className="result-section">
              <div className="result-heading">New investments</div>
              <div className="result-value">
                {formatCurrency(results.totalNewInvestments, 0)}
              </div>
              <p className="result-copy">
                Based on any regular payments you’ve selected.
              </p>
            </div>

            <div className="result-section">
              <div className="result-heading">Investment growth</div>
              <div className="result-value">
                {formatCurrency(results.investmentGrowth, 0)}
              </div>
            </div>

            <div className="result-divider" />

            <div className="result-note-title">PLEASE NOTE:</div>
            <p className="result-note-copy">
              Nothing on this site is personalised advice or a recommendation.
              If you need a personalised recommendation based on your personal
              circumstances you should seek financial advice.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
